/**
 * IndexedDB storage service for persisting library data and file system handles
 */

import type { FileSystemDirectoryHandle, FileSystemFileHandle, FileSystemHandle } from '../types/filesystem';

// Database configuration
const DB_NAME = 'PlayaLibraryDB';
const DB_VERSION = 2; // Incremented to add album art support

// Object store names
const STORES = {
    LIBRARY_METADATA: 'libraryMetadata',
    MUSIC_FILES: 'musicFiles',
    FILE_HANDLES: 'fileHandles',
    ALBUM_ART: 'albumArt'
} as const;

// Types for serializable data
export interface SerializableMusicFile {
    name: string;
    path: string;
    size: number;
    lastModified: number;
    duration?: number;
    artist?: string;
    album?: string;
    title?: string;
    year?: string;
    trackNumber?: number;
    albumArt?: {
        data: string; // Changed to base64 string instead of ArrayBuffer
        mimeType: string;
        description?: string;
        pictureType?: number;
    };
    // New field for file-based album art reference
    albumArtFile?: {
        id: string; // Reference to album art file in storage
        filename: string;
        mimeType: string;
    };
}

export interface LibraryMetadata {
    id: string;
    rootDirectory: string;
    lastScanDate: number;
    totalFiles: number;
    version: number;
}

export interface SerializableFileHandle {
    path: string;
    type: 'file' | 'directory';
    name: string;
    // We'll store the actual handle separately and link by path
}

export interface AlbumArtFile {
    id: string; // Unique identifier (generated hash)
    filename: string;
    path: string; // Full path to the album art file
    directoryPath: string; // Directory containing this file
    mimeType: string;
    size: number;
    lastModified: number;
    data: ArrayBuffer; // The actual image data
    fileHandle: FileSystemFileHandle; // Optional persisted file handle
}

class StorageService {
    private db: IDBDatabase | null = null;

    /**
     * Initialize the IndexedDB database
     */
    async init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                reject(new Error(`Failed to open database: ${request.error?.message}`));
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                this.createObjectStores(db);
            };
        });
    }

    /**
     * Create object stores for the database
     */
    private createObjectStores(db: IDBDatabase): void {
        // Library metadata store
        if (!db.objectStoreNames.contains(STORES.LIBRARY_METADATA)) {
            db.createObjectStore(STORES.LIBRARY_METADATA, { keyPath: 'id' });
        }

        // Music files store
        if (!db.objectStoreNames.contains(STORES.MUSIC_FILES)) {
            const musicFilesStore = db.createObjectStore(STORES.MUSIC_FILES, { keyPath: 'path' });
            musicFilesStore.createIndex('artist', 'artist', { unique: false });
            musicFilesStore.createIndex('album', 'album', { unique: false });
            musicFilesStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // File handles store - stores actual FileSystemHandle objects
        if (!db.objectStoreNames.contains(STORES.FILE_HANDLES)) {
            db.createObjectStore(STORES.FILE_HANDLES, { keyPath: 'path' });
        }

        // Album art store - stores image files found in directories
        if (!db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            const albumArtStore = db.createObjectStore(STORES.ALBUM_ART, { keyPath: 'id' });
            albumArtStore.createIndex('directoryPath', 'directoryPath', { unique: false });
            albumArtStore.createIndex('filename', 'filename', { unique: false });
        }
    }

    /**
     * Save library metadata
     */
    async saveLibraryMetadata(metadata: LibraryMetadata): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.LIBRARY_METADATA], 'readwrite');
            const store = transaction.objectStore(STORES.LIBRARY_METADATA);
            const request = store.put(metadata);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Load library metadata
     */
    async loadLibraryMetadata(): Promise<LibraryMetadata | null> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.LIBRARY_METADATA], 'readonly');
            const store = transaction.objectStore(STORES.LIBRARY_METADATA);
            const request = store.get('main'); // Use 'main' as the primary library ID

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result || null);
        });
    }

    /**
     * Save music files data
     */
    async saveMusicFiles(musicFiles: SerializableMusicFile[]): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.MUSIC_FILES], 'readwrite');
            const store = transaction.objectStore(STORES.MUSIC_FILES);

            // Clear existing files first
            const clearRequest = store.clear();
            clearRequest.onsuccess = () => {
                // console.log('Cleared existing music files from storage');
                // Add all music files
                let completed = 0;
                let hasError = false;

                if (musicFiles.length === 0) {
                    // console.log('No music files to save');
                    resolve();
                    return;
                }

                // console.log(`Starting to save ${musicFiles.length} music files`);
                musicFiles.forEach((file) => {
                    try {
                        const addRequest = store.put(file); // Changed from add to put
                        addRequest.onerror = () => {
                            if (!hasError) {
                                hasError = true;
                                console.error('Error saving music file:', file.path, addRequest.error);
                                reject(addRequest.error);
                            }
                        };
                        addRequest.onsuccess = () => {
                            completed++;
                            // console.log(`Saved music file ${completed}/${musicFiles.length}: ${file.path}`);
                            if (completed === musicFiles.length && !hasError) {
                                resolve();
                            }
                        };
                    } catch (error) {
                        console.error(`Error preparing to save file ${file.path}:`, error);
                        if (!hasError) {
                            hasError = true;
                            reject(error);
                        }
                    }
                });
            };
            clearRequest.onerror = () => {
                console.error('Error clearing music files storage:', clearRequest.error);
                reject(clearRequest.error);
            };
        });
    }

    /**
     * Load all music files
     */
    async loadMusicFiles(): Promise<SerializableMusicFile[]> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.MUSIC_FILES], 'readonly');
            const store = transaction.objectStore(STORES.MUSIC_FILES);
            const request = store.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const result = request.result || [];
                resolve(result);
            };
        });
    }

    /**
     * Save file system handle
     */
    async saveFileHandle(path: string, handle: FileSystemHandle): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.FILE_HANDLES], 'readwrite');
            const store = transaction.objectStore(STORES.FILE_HANDLES);

            const handleData = {
                path,
                handle,
                type: handle.kind,
                name: handle.name,
                savedAt: Date.now()
            };

            const request = store.put(handleData);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Load file system handle by path
     */
    async loadFileHandle(path: string): Promise<FileSystemHandle | null> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.FILE_HANDLES], 'readonly');
            const store = transaction.objectStore(STORES.FILE_HANDLES);
            const request = store.get(path);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const result = request.result;
                resolve(result ? result.handle : null);
            };
        });
    }

    /**
     * Load the root directory handle
     */
    async loadRootDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
        const handle = await this.loadFileHandle('ROOT_DIRECTORY');
        return handle as FileSystemDirectoryHandle | null;
    }

    /**
     * Save the root directory handle
     */
    async saveRootDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
        await this.saveFileHandle('ROOT_DIRECTORY', handle);
    }

    /**
     * Load all file handles
     */
    async loadAllFileHandles(): Promise<Map<string, FileSystemHandle>> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.FILE_HANDLES], 'readonly');
            const store = transaction.objectStore(STORES.FILE_HANDLES);
            const request = store.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const handles = new Map<string, FileSystemHandle>();
                const results = request.result || [];

                results.forEach(item => {
                    handles.set(item.path, item.handle);
                });

                resolve(handles);
            };
        });
    }

    /**
     * Clear all stored data
     */
    async clearAll(): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        return new Promise((resolve, reject) => {
            // Only include stores that actually exist
            const storesToClear: string[] = [STORES.LIBRARY_METADATA, STORES.MUSIC_FILES, STORES.FILE_HANDLES];
            if (this.db!.objectStoreNames.contains(STORES.ALBUM_ART)) {
                storesToClear.push(STORES.ALBUM_ART);
            }

            const transaction = this.db!.transaction(storesToClear, 'readwrite');

            let completed = 0;

            storesToClear.forEach(storeName => {
                const store = transaction.objectStore(storeName);
                const request = store.clear();

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    completed++;
                    if (completed === storesToClear.length) {
                        resolve();
                    }
                };
            });
        });
    }

    private async attachAlbumArtHandle(art: AlbumArtFile): Promise<AlbumArtFile> {
        const albumArt: AlbumArtFile = { ...art };

        try {
            const handle = await this.loadFileHandle(albumArt.path);
            if (handle && handle.kind === 'file') {
                albumArt.fileHandle = handle as FileSystemFileHandle;
            }
        } catch (error) {
            console.warn(`Failed to load album art handle for ${albumArt.path}:`, error);
        }

        return albumArt;
    }

    /**
     * Save album art file to storage
     */
    async saveAlbumArt(albumArt: AlbumArtFile): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        // Check if the album art store exists
        if (!this.db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            console.warn('Album art store does not exist - database needs upgrade. Skipping save.');
            return;
        }

        if (albumArt.fileHandle) {
            try {
                await this.saveFileHandle(albumArt.path, albumArt.fileHandle);
            } catch (error) {
                console.warn(`Failed to persist album art handle for ${albumArt.path}:`, error);
            }
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.ALBUM_ART], 'readwrite');
            const store = transaction.objectStore(STORES.ALBUM_ART);
            const request = store.put(albumArt);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Load album art file by ID
     */
    async loadAlbumArt(id: string): Promise<AlbumArtFile | null> {
        if (!this.db) throw new Error('Database not initialized');

        // Check if the album art store exists
        if (!this.db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            console.warn('Album art store does not exist - database needs upgrade.');
            return null;
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.ALBUM_ART], 'readonly');
            const store = transaction.objectStore(STORES.ALBUM_ART);
            const request = store.get(id);

            request.onerror = () => reject(request.error);
            request.onsuccess = async () => {
                const stored = request.result as AlbumArtFile | undefined;

                if (!stored) {
                    resolve(null);
                    return;
                }

                const albumArt = await this.attachAlbumArtHandle(stored);
                resolve(albumArt);
            };
        });
    }

    /**
     * Load all album art files for a directory
     */
    async loadAlbumArtByDirectory(directoryPath: string): Promise<AlbumArtFile[]> {
        if (!this.db) throw new Error('Database not initialized');

        // Check if the album art store exists
        if (!this.db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            console.warn('Album art store does not exist - database needs upgrade.');
            return [];
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.ALBUM_ART], 'readonly');
            const store = transaction.objectStore(STORES.ALBUM_ART);
            const index = store.index('directoryPath');
            const request = index.getAll(directoryPath);

            request.onerror = () => reject(request.error);
            request.onsuccess = async () => {
                const stored = (request.result || []) as AlbumArtFile[];
                const enriched = await Promise.all(stored.map(art => this.attachAlbumArtHandle(art)));
                resolve(enriched);
            };
        });
    }

    /**
     * Delete album art file by ID
     */
    async deleteAlbumArt(id: string): Promise<void> {
        if (!this.db) throw new Error('Database not initialized');

        // Check if the album art store exists
        if (!this.db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            console.warn('Album art store does not exist - database needs upgrade. Skipping delete.');
            return;
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.ALBUM_ART], 'readwrite');
            const store = transaction.objectStore(STORES.ALBUM_ART);
            const request = store.delete(id);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Get all album art files
     */
    async loadAllAlbumArt(): Promise<AlbumArtFile[]> {
        if (!this.db) throw new Error('Database not initialized');

        // Check if the album art store exists
        if (!this.db.objectStoreNames.contains(STORES.ALBUM_ART)) {
            console.warn('Album art store does not exist - database needs upgrade.');
            return [];
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORES.ALBUM_ART], 'readonly');
            const store = transaction.objectStore(STORES.ALBUM_ART);
            const request = store.getAll();

            request.onerror = () => reject(request.error);
            request.onsuccess = async () => {
                const stored = (request.result || []) as AlbumArtFile[];
                const enriched = await Promise.all(stored.map(art => this.attachAlbumArtHandle(art)));
                resolve(enriched);
            };
        });
    }

    /**
     * Check if the database has stored library data
     */
    async hasStoredLibrary(): Promise<boolean> {
        try {
            const metadata = await this.loadLibraryMetadata();
            return metadata !== null;
        } catch {
            return false;
        }
    }

    /**
     * Close the database connection
     */
    close(): void {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }
}

// Export singleton instance
export const storageService = new StorageService();